// 세션 화면의 화면 품질·FPS 선택(설계서 §9.5). 2026-10-12 부터 도구 막대가 아니라 [⋯] 메뉴 안에 그린다(`SessionMoreMenu`).
//
// 구성: [화질 ▼] [FPS ▼] — 그리고 화질 목록 안에 **자동**이 한 항목으로 들어간다.
// 자동을 별도 체크박스로 두지 않은 이유: 사용자에게 "화질을 어떻게 정할까" 는 **하나의 선택**이고,
// 칸을 둘로 나누면 "자동인데 화질도 골라져 있는" 모순된 상태가 화면에 보인다.
//
// 🔴 1920 이하 화면에서 "높음" 은 **해상도를 올리지 않는다**(업스케일 금지 §11.2) — 비트레이트만 오른다.
//    그 사실을 툴팁으로 말해 주지 않으면 "바꿨는데 왜 그대로냐" 가 된다.
import { PRESETS, FPS_CHOICES, type PresetId } from '../quality/protocol';
import type { QualityView } from '../quality/useQuality';

/** 화질 선택의 값 — 프리셋 3종 + 자동. */
const AUTO_VALUE = 'auto';

export function QualityControls({
  quality,
  refocus,
}: {
  quality: QualityView;
  refocus: () => void;
}) {
  // 구버전 에이전트(ctrl 채널 없음)면 통째로 숨긴다 — 다른 기능들과 같은 규칙.
  if (!quality.available) {
    return null;
  }

  const { spec, applied, auto, autoLabel, error, resolutionCapped, expectedDims } = quality;

  // 적용 결과가 있으면 그것을 보여 준다(요청값이 아니라 **실제 값**).
  const appliedText = applied
    ? `${applied.width}×${applied.height} · ${applied.fps}fps · ${applied.kbps.toLocaleString()}kbps`
    : '적용 대기…';

  const presetTitle = (p: PresetId): string => {
    const def = PRESETS.find((x) => x.id === p)!;
    const capped = resolutionCapped && p === spec.preset;
    return (
      `${def.label} — 해상도 상한 ${def.maxEdge}px` +
      (capped ? ' (이 화면은 이미 상한 이하라 해상도는 그대로이고 선명도만 달라집니다)' : '')
    );
  };

  return (
    <span
      style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}
      title={
        `현재 적용: ${appliedText}` +
        (expectedDims ? `\n이 설정의 예상 해상도: ${expectedDims.width}×${expectedDims.height}` : '') +
        (error ? `\n⚠ ${error}` : '')
      }
    >
      {/* "화질" 제목은 [⋯] 메뉴의 구역 머리가 단다(2026-10-12 — 막대에서 메뉴 안으로 옮김). */}
      <select
        className="ghost"
        aria-label="화면 품질"
        value={auto ? AUTO_VALUE : spec.preset}
        onChange={(e) => {
          const v = e.target.value;
          if (v === AUTO_VALUE) {
            quality.setAuto(true);
          } else {
            quality.setPreset(v as PresetId);
          }
          refocus();
        }}
        style={{ padding: '2px 4px' }}
      >
        {PRESETS.map((p) => (
          <option key={p.id} value={p.id} title={presetTitle(p.id)}>
            {p.label}
          </option>
        ))}
        <option value={AUTO_VALUE} title="회선 상태에 따라 화질과 FPS 를 스스로 올리고 내립니다">
          자동
        </option>
      </select>

      <select
        className="ghost"
        aria-label="초당 프레임"
        value={spec.fps}
        // 자동일 때는 FPS 도 자동이 정한다 → 직접 고르면 자동이 풀린다(setFps 안에서 해제).
        onChange={(e) => {
          quality.setFps(Number(e.target.value));
          refocus();
        }}
        style={{ padding: '2px 4px' }}
      >
        {FPS_CHOICES.map((f) => (
          <option key={f} value={f}>
            {f}fps
          </option>
        ))}
      </select>

      {auto && (
        <span style={{ fontSize: 11, opacity: 0.7 }} title="자동 조절이 지금 서 있는 단계">
          ({autoLabel})
        </span>
      )}
      {error && (
        <span style={{ fontSize: 11, color: '#e06c6c' }} title={error}>
          ⚠
        </span>
      )}
    </span>
  );
}
